// Calculation utilities for invoice line items
// Following the same pattern as admin dashboard

export enum ServiceType {
  TOW_BASE = 'TOW_BASE',
  TOW_HOURLY = 'TOW_HOURLY',
  TOW_QUARTER_HOUR = 'TOW_QUARTER_HOUR',
  TOW_HALF_HOUR = 'TOW_HALF_HOUR',
  TOW_HOUR_MINIMUM = 'TOW_HOUR_MINIMUM',
  TOW_PER_MILE = 'TOW_PER_MILE',
  STORAGE_BASE = 'STORAGE_BASE',
  STORAGE_DAILY = 'STORAGE_DAILY',
  LABOR = 'LABOR',
}

export enum TowType {
  ANY = 'ANY',
  LIGHT = 'LIGHT',
  MEDIUM = 'MEDIUM',
  HEAVY = 'HEAVY',
  CLASS_D = 'CLASS_D',
  MOTORCYCLE = 'MOTORCYCLE',
  TRAILER = 'TRAILER',
  TRACTOR = 'TRACTOR',
  EQUIPMENT = 'EQUIPMENT',
  CONTAINER = 'CONTAINER',
  MATERIAL = 'MATERIAL',
  OTHER = 'OTHER',
}

export interface Rate {
  id: string;
  customerId: string;
  serviceType: ServiceType;
  any?: number;
  light?: number;
  medium?: number;
  heavy?: number;
  classD?: number;
  motorcycle?: number;
  trailer?: number;
  tractor?: number;
  equipment?: number;
  container?: number;
  material?: number;
  other?: number;
}

export interface Dispatch {
  id: string;
  account: string;
  towType?: TowType;
  completionTime?: string;
  createdAt: string;
  startedAt?: string;
  arrivedAt?: string;
  mileage?: number;
}

export interface Customer {
  id: string;
  towBillingStarts?: 'NEW' | 'PENDING' | 'DISPATCHED' | 'ON_SCENE' | 'TOWING' | 'COMPLETED' | 'CANCELED';
}

export interface Impound {
  impoundDate: string;
  releasedDate?: string | null;
}

/**
 * Calculate the number of days between two dates
 */
export function calculateDaysHeld(
  startDate: string,
  endDate: string | null = null
): number {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get vehicle type specific pricing from rate
 */
export function getVehicleTypePrice(rate: Rate, towType: TowType): number {
  switch (towType) {
    case TowType.LIGHT:
      return rate.light || rate.any || 0;
    case TowType.MEDIUM:
      return rate.medium || rate.any || 0;
    case TowType.HEAVY:
      return rate.heavy || rate.any || 0;
    case TowType.CLASS_D:
      return rate.classD || rate.any || 0;
    case TowType.MOTORCYCLE:
      return rate.motorcycle || rate.any || 0;
    case TowType.TRAILER:
      return rate.trailer || rate.any || 0;
    case TowType.TRACTOR:
      return rate.tractor || rate.any || 0;
    case TowType.EQUIPMENT:
      return rate.equipment || rate.any || 0;
    case TowType.CONTAINER:
      return rate.container || rate.any || 0;
    case TowType.MATERIAL:
      return rate.material || rate.any || 0;
    case TowType.OTHER:
      return rate.other || rate.any || 0;
    case TowType.ANY:
    default:
      return rate.any || 0;
  }
}

/**
 * Get the billing start time based on customer's towBillingStarts setting
 */
export function getBillingStartTime(
  dispatch: Dispatch,
  customer?: Customer
): Date {
  const towBillingStarts = customer?.towBillingStarts || 'DISPATCHED';

  switch (towBillingStarts) {
    case 'NEW':
      return new Date(dispatch.createdAt);
    case 'PENDING':
      return new Date(dispatch.createdAt);
    case 'DISPATCHED':
      return new Date(dispatch.startedAt || dispatch.createdAt);
    case 'ON_SCENE':
      return new Date(
        dispatch.arrivedAt || dispatch.startedAt || dispatch.createdAt
      );
    case 'TOWING':
      return new Date(
        dispatch.arrivedAt || dispatch.startedAt || dispatch.createdAt
      );
    case 'COMPLETED':
      return new Date(dispatch.completionTime || dispatch.createdAt);
    case 'CANCELED':
      return new Date(dispatch.completionTime || dispatch.createdAt);
    default:
      return new Date(dispatch.startedAt || dispatch.createdAt);
  }
}

